import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs/esm';
import { cloneDeep } from 'lodash-es';

import { AuthenticationMethod, Country, UserSubType, type User } from '@models';
import { DateFormatService } from './date-format.service';
import { ModalService } from './modal.service';
import { ToastService } from './toast.service';

@Service()
export class AccountService {
  modalService = inject(ModalService);
  toastService = inject(ToastService);
  translate = inject(TranslateService);
  dateFormatService = inject(DateFormatService);

  editUserImage = async (userRid: number) => {
    const { EditProfileImageModalComponent } =
      await import('../../modules/account/components/edit-profile-image-modal.component');

    return this.modalService.open(
      EditProfileImageModalComponent,
      {
        userRid,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  changePassword = async (userRid: number, rootUsersId: number) => {
    const { ChangePasswordModalComponent } =
      await import('../../modules/account/components/change-password-modal.component');

    return this.modalService.open(
      ChangePasswordModalComponent,
      {
        userRid,
        rootUsersId,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userImport = async (guests: boolean) => {
    const { ImportUserModalComponent } =
      await import('../../modules/account/components/import-user-modal.component');

    return this.modalService.open(
      ImportUserModalComponent,
      {
        guests,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userSupport = async () => {
    const { UserSupportModalComponent } =
      await import('../../modules/account/components/user-support-modal.component');

    return this.modalService.open(
      UserSupportModalComponent,
      {},
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userSendLoginInfo = async (users: User[]) => {
    const { SendLoginInfoModalComponent } =
      await import('../../modules/account/components/send-login-info-modal.component');

    return this.modalService.open(
      SendLoginInfoModalComponent,
      {
        users: cloneDeep(users),
        onlyFinma: !users.some(
          (user) =>
            user.authenticationMethod !== AuthenticationMethod.FINMA_SSO &&
            user.subtype !== UserSubType.GUEST
        ),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userEnable = async (users: User[]) => {
    const { EnableUserModalComponent } =
      await import('../../modules/account/components/enable-user-modal.component');

    return this.modalService.open(
      EnableUserModalComponent,
      {
        users: cloneDeep(users),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userAddMemberships = async (user: User) => {
    const { AddMembershipsModalComponent } =
      await import('../../modules/account/components/add-memberships-modal.component');

    return this.modalService.open(
      AddMembershipsModalComponent,
      {
        user: cloneDeep(user),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userSessionExpiration = async () => {
    const { SessionExpirationModalComponent } =
      await import('../../modules/account/components/session-expiration-modal.component');

    return this.modalService.open(
      SessionExpirationModalComponent,
      {},
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userEditSignatureImage = async (user: Pick<User, 'rid' | 'updateDate'>) => {
    const { EditSignatureImageModalComponent } =
      await import('../../modules/account/components/edit-signature-image-modal.component');

    return this.modalService.open(
      EditSignatureImageModalComponent,
      {
        user: cloneDeep(user),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  userEditNotifications = async (user: User) => {
    const { EditUserNotificationsModalComponent } =
      await import('../../modules/account/components/edit-user-notifications-modal.component');

    return this.modalService.open(
      EditUserNotificationsModalComponent,
      {
        user: cloneDeep(user),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  editUser = async (user: User, languages: string[], countries: Country[]) => {
    const { EditUserModalComponent } =
      await import('../../modules/account/components/edit-user-modal.component');

    return this.modalService.open(
      EditUserModalComponent,
      {
        user: cloneDeep(user),
        languages,
        countries,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  userNewFinma = async (userSubtype: number) => {
    const { NewFinmaUserModalComponent } =
      await import('../../modules/account/components/new-finma-user-modal.component');

    return this.modalService.open(
      NewFinmaUserModalComponent,
      {
        userSubtype,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  userNewUser = async (
    userSubtype: UserSubType,
    isSigningGuest?: boolean,
    allowOneTimeGuest?: boolean,
    existingGuests?: User[]
  ) => {
    const { NewUserModalComponent } =
      await import('../../modules/account/components/new-user-modal.component');

    return this.modalService.open(
      NewUserModalComponent,
      {
        userSubtype,
        isSigningGuest,
        allowOneTimeGuest,
        existingGuests,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  differentSessionDialog = async () => {
    const { SessionCheckModalComponent } =
      await import('../../modules/account/components/session-check-modal.component');

    return this.modalService.open(
      SessionCheckModalComponent,
      {},
      {
        modalDialogClass: 'lg-modal',
        keyboard: false,
      }
    );
  };

  showRemovedSessionToast = (removedSession: { sessionStarted: number }) =>
    this.showTimestampToast(
      removedSession.sessionStarted,
      'WEB_ALERT_REMOVED_SESSION'
    );

  showLastLoginToast = (lastLogin: number) =>
    this.showTimestampToast(lastLogin, 'WEB_ALERT_LAST_LOGIN');

  showTimestampToast = (timestamp: number, translationKey: string) => {
    this.toastService.clear();

    setTimeout(() => {
      const timeString = dayjs(timestamp).format(
        this.dateFormatService.dateTimeFormat()
      );

      this.toastService.show({
        text: this.translate.instant(translationKey, { value: timeString }),
      });
    }, 2000);
  };
}
